import type { RightyFlowNodeReference, RightyPrompt } from '../types'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

/**
 * Default fallback prompts shown when no contextual prompts are available
 */
export const defaultPrompts: RightyPrompt[] = [
  {
    label: 'How can I help?',
    drawerLabel: 'What can Righty help with?',
    prompt: 'What can you help me with on this page?'
  },
  {
    label: 'Quick overview',
    drawerLabel: 'Give me a quick overview',
    prompt: "Give me a quick overview of what I'm looking at and what I can do here."
  },
  {
    label: 'Best practices',
    drawerLabel: 'What are the best practices here?',
    prompt: 'What are the best practices for using this feature effectively?'
  },
  {
    label: 'Get started',
    drawerLabel: 'Help me get started',
    prompt: 'Help me get started with the most important thing I should do first.'
  }
]

/**
 * Merges prompts from multiple sources with priority ordering.
 * Priority: dynamic > route > defaults
 *
 * @param dynamicPrompts - Prompts registered by current component (highest priority)
 * @param routePrompts - Prompts from route meta
 * @param maxCount - Maximum number of prompts to return (default: 4 for drawer)
 * @returns Merged array of prompts, limited to maxCount
 */
export function mergePrompts(
  dynamicPrompts: RightyPrompt[],
  routePrompts: RightyPrompt[],
  maxCount: number = 4
): RightyPrompt[] {
  // Start with dynamic prompts (highest priority)
  const merged: RightyPrompt[] = [...dynamicPrompts]

  // Add route prompts that don't duplicate dynamic prompt labels
  for (const routePrompt of routePrompts) {
    if (!merged.some((p) => p.label === routePrompt.label)) {
      merged.push(routePrompt)
    }
  }

  // If we still need more prompts, pad with defaults
  if (merged.length < maxCount) {
    for (const defaultPrompt of defaultPrompts) {
      if (!merged.some((p) => p.label === defaultPrompt.label)) {
        merged.push(defaultPrompt)
      }
      if (merged.length >= maxCount) {
        break
      }
    }
  }

  // Return limited count
  return merged.slice(0, maxCount)
}

/**
 * Extracts route prompts from the current route's meta
 *
 * @param route - The current route object
 * @returns Array of prompts from route meta, or empty array
 */
export function getRoutePrompts(route: RouteLocationNormalizedLoaded): RightyPrompt[] {
  return (route.meta?.rightyPrompts as RightyPrompt[]) || []
}

/**
 * Gets contextual suggestions for the drawer.
 * Always returns exactly 4 prompts for consistent UI.
 *
 * @param dynamicPrompts - Prompts registered by current component
 * @param route - The current route object
 * @returns Array of exactly 4 prompts for the drawer
 */
export function getDrawerSuggestions(
  dynamicPrompts: RightyPrompt[],
  route: RouteLocationNormalizedLoaded
): RightyPrompt[] {
  const routePrompts = getRoutePrompts(route)
  return mergePrompts(dynamicPrompts, routePrompts, 4)
}

/**
 * Gets contextual prompts for the external chips (above the trigger button).
 * Returns up to 3 prompts, but only if there are actual contextual prompts.
 * Does NOT include defaults for external chips (they should be meaningful).
 *
 * @param dynamicPrompts - Prompts registered by current component
 * @param route - The current route object
 * @returns Array of up to 3 prompts, or empty if no contextual prompts
 */
export function getExternalChipPrompts(
  dynamicPrompts: RightyPrompt[],
  route: RouteLocationNormalizedLoaded
): RightyPrompt[] {
  const routePrompts = getRoutePrompts(route)

  // Start with dynamic prompts (highest priority)
  const merged: RightyPrompt[] = [...dynamicPrompts]

  // Add route prompts that don't duplicate
  for (const routePrompt of routePrompts) {
    if (!merged.some((p) => p.label === routePrompt.label)) {
      merged.push(routePrompt)
    }
  }

  // Return up to 3, but don't pad with defaults for external chips
  return merged.slice(0, 3)
}

/** What a step kind is for, so its ideas ask the obvious next questions. Kinds are the flow builder's kind words. */
const FLOW_STEP_IDEAS: Partial<Record<string, RightyPrompt[]>> = {
  Question: [
    {
      label: 'Answer rate',
      drawerLabel: 'How is this question performing?',
      prompt: 'How is this question performing? Show its asks, answer rate and how answers split.'
    },
    {
      label: 'Improve it',
      drawerLabel: 'How could I improve this question?',
      prompt:
        'How could I improve this question and its answer options to get more people to answer? Suggest versions I can A/B test.'
    }
  ],
  Router: [
    {
      label: 'Who goes where',
      drawerLabel: 'Who goes down each path?',
      prompt: 'Who goes down each path from this router, and how many people take each one?'
    },
    {
      label: 'Check the rules',
      drawerLabel: 'Check these routing rules for gaps',
      prompt: 'Check the routing rules on this step for gaps or overlaps. Who could fall through?'
    }
  ],
  Condition: [
    {
      label: 'Who goes where',
      drawerLabel: 'Who goes down each path?',
      prompt: 'Who matches this condition and who does not, and how many people take each path?'
    },
    {
      label: 'Check the logic',
      drawerLabel: 'Check this condition for gaps',
      prompt: 'Check this condition for gaps or mistakes. Could anyone end up on the wrong path?'
    }
  ],
  Offer: [
    {
      label: 'Conversion',
      drawerLabel: 'How is this offer converting?',
      prompt: 'How is this offer converting, and who sees it?'
    },
    {
      label: 'Improve it',
      drawerLabel: 'How could I improve this offer?',
      prompt: 'How could I improve this offer to get more people to click it? Suggest versions I can A/B test.'
    }
  ],
  Form: [
    {
      label: 'Conversion',
      drawerLabel: 'How is this form converting?',
      prompt: 'How is this form converting, and who sees it?'
    },
    {
      label: 'Improve it',
      drawerLabel: 'How could I improve this form?',
      prompt: 'How could I improve this form to get more people to submit it?'
    }
  ],
  Split: [
    {
      label: 'Winner',
      drawerLabel: 'Which side of this split is winning?',
      prompt: 'Which side of this split test is winning, and is the difference meaningful yet?'
    }
  ]
}

/**
 * The drawer's ideas when the composer carries flow steps from the flow builder: questions about those steps, which
 * ride along with the message, instead of the page's ideas. Exactly 4 prompts, like `getDrawerSuggestions`.
 */
export function getFlowNodeSuggestions(references: RightyFlowNodeReference[]): RightyPrompt[] {
  const [only, ...others] = references
  if (!only) {
    return []
  }
  if (others.length > 0) {
    return [
      {
        label: 'Explain',
        drawerLabel: 'Explain how these steps connect',
        prompt: 'Explain what these steps do and how visitors move between them.'
      },
      {
        label: 'Compare',
        drawerLabel: 'Compare how these steps perform',
        prompt: 'Compare how these steps perform. Where do people drop off?'
      },
      {
        label: 'Find problems',
        drawerLabel: 'Find problems in these steps',
        prompt: 'Check these steps for problems, such as broken logic, dead ends or confusing copy.'
      },
      {
        label: 'Improve',
        drawerLabel: 'How could I improve this part of the flow?',
        prompt: 'How could I improve this part of the flow?'
      }
    ]
  }
  return [
    {
      label: 'Explain',
      drawerLabel: 'Explain what this step does',
      prompt: 'Explain what happens at this step and where visitors go next.'
    },
    ...(FLOW_STEP_IDEAS[only.kind] ?? []),
    {
      label: 'How people get here',
      drawerLabel: 'How do people reach this step?',
      prompt: 'Which paths lead people to this step, and how many reach it?'
    },
    {
      label: 'Find problems',
      drawerLabel: 'Find problems with this step',
      prompt: 'Check this step for problems, such as broken logic, dead ends or confusing copy.'
    }
  ].slice(0, 4)
}
